import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import { CheckboxControl } from '@liveconnect/components'
import { Icon } from '@liveconnect/icons'
import { useNavigate } from 'react-router'

import usePublishedMessages from '../../../../core/publishedMessages/usePublishedMessages'
import { PublishedMessages } from '../../../../core/publishedMessages/types'
import { Main } from '../../../../components/Main'
import useCulture from '../../../../utils/culture'
import DetailModal from './DetailModal'
import useNotifications from '../../../../utils/notifications/useNotifications'
import ContentTable from '../../../../components/ContentTable'
import FilterPanel from './FilterPanel'

import './styles.scss'

const PublishedMessagesScreen: FC = () => {
  const { t } = useTranslation()
  const notify = useNotifications()
  const { formatDate } = useCulture()
  const navigate = useNavigate()

  const [selectedMessage, setSelectedMessage] = useState<string[]>([])
  const [showDetail, setShowDetail] = useState<PublishedMessages | null>(null)
  const [showFilters, setShowFilters] = useState<boolean>(false)

  const {
    list,
    isLoaded,
    statusSelected,
    serviceSelected,
    fetchList,
    clearMessageListStore,
    republishMessages,
    fetchMessageStatusList,
    fetchServiceList,
  } = usePublishedMessages()

  const handlePublishedMessageSelected = (key: string) => {
    const index = selectedMessage.indexOf(key)
    if (index !== -1) {
      selectedMessage.splice(index, 1)
    } else {
      selectedMessage.push(key)
    }
    setSelectedMessage([...selectedMessage])
  }

  const renderCheckboxCell = useCallback(
    (row: PublishedMessages) => {
      if (!row || !row.id) return ''
      return (
        <div className="PublishedMessages__checkbox__cell">
          <CheckboxControl
            id={row.key}
            value={selectedMessage.includes(row.key)}
            name={row.id}
            onChange={() => handlePublishedMessageSelected(row.key)}
            label=""
            hiddenLabel={true}
          />
        </div>
      )
    },
    [handlePublishedMessageSelected, selectedMessage]
  )

  const renderIdCell = (row: PublishedMessages) => {
    if (!row || !row.id) return ''
    return (
      <div className="PublishedMessages__id" onClick={() => setShowDetail(row)}>
        <div className="PublishedMessages__id__cell">{row.id}</div>
      </div>
    )
  }

  const renderDateTimeCell = (dateTime: string) => {
    if (!dateTime) return ''
    return formatDate(new Date(dateTime as string) as Date, 'LONG_DATE_TIME')
  }

  const columns: ColumnDef<PublishedMessages>[] = useMemo(
    () => [
      {
        accessorKey: 'isSelected',
        header: '',
        cell: ({ row }) => renderCheckboxCell(row.original),
      },
      {
        accessorKey: 'id',
        header: t('dashboard.publishedMessages.list.table.id'),
        cell: ({ row }) => renderIdCell(row.original),
      },
      {
        accessorKey: 'eventType',
        header: t('dashboard.publishedMessages.list.table.eventType'),
      },
      {
        accessorKey: 'retries',
        header: t('dashboard.publishedMessages.list.table.retries'),
      },
      {
        accessorKey: 'createdAt',
        header: t('dashboard.publishedMessages.list.table.createdAt'),
        cell: ({ row }) => renderDateTimeCell(row.original.createdAt),
      },
      {
        accessorKey: 'expires',
        header: t('dashboard.publishedMessages.list.table.expires'),
        cell: ({ row }) => renderDateTimeCell(row.original.expires),
      },
    ],
    [renderCheckboxCell, renderDateTimeCell, t]
  )

  // Table - END
  const handleRepublishMessage = async () => {
    try {
      await republishMessages(selectedMessage)
      notify.success(t('dashboard.publishedMessages.republish.successFeedback'))
    } catch (e) {
    } finally {
      setSelectedMessage([])
    }
  }

  useEffect(() => {
    if (statusSelected) {
      fetchList({
        messageStatusType: statusSelected,
        serviceName: serviceSelected,
      })
    }
  }, [statusSelected, serviceSelected])

  useEffect(() => {
    fetchMessageStatusList()
    fetchServiceList()
    return () => {
      clearMessageListStore()
    }
  }, [])

  return (
    <>
      <Main ariaLabelledby="user-list-title" className="PublishedMessages">
        <div className="d-flex justify-content-between align-items-end">
          <div>
            <h1 id="user-list-title" className="h3">
              {t('dashboard.title')}
            </h1>
          </div>
          <div className="d-flex flex-column">
            <div>
              <button
                className="btn btn-link btn-ternary"
                onClick={() => navigate('..')}
              >
                <Icon name="chevron_left" />
                {t('dashboard.details.goback')}
              </button>
            </div>
            <button
              className="btn btn-primary btn-republish mb-3"
              onClick={() => handleRepublishMessage()}
              disabled={!selectedMessage.some((x) => !!x)}
            >
              {t('dashboard.publishedMessages.republish')}
            </button>
          </div>
        </div>

        <div className="PublishedMessages__list__searchs"></div>
        {statusSelected && (
          <section className="mt-3">
            <div className="ReceivedMessages__list">
              <ContentTable
                title={t('dashboard.receivedMessages.list.table.title')}
                columns={columns}
                data={list}
                emptyText={t('dashboard.receivedMessages.emptyList')}
                isLoaded={isLoaded}
                onFilterClick={() => setShowFilters(true)}
                filterLabel={t('common.filters')}
                isFilterActive={true}
              />
            </div>
          </section>
        )}
      </Main>
      <FilterPanel isOpen={showFilters} onClose={() => setShowFilters(false)} />
      {showDetail && (
        <DetailModal
          onClose={() => setShowDetail(null)}
          publishedMessage={showDetail}
        />
      )}
    </>
  )
}

export default PublishedMessagesScreen
